import { computed, ref } from "vue";

const emptyFilterOptions = () => ({
  url_path: [],
  query_params: [],
  url_hash: [],
  url_host: [],
  enabled: true,
});

/**
 * Manages the filter-builder rows for a trigger's `filter_options`, including
 * the derived `filterOptions`/`filterModeDisabled` accessors and row CRUD.
 *
 * @param {import("vue").Ref<object>} editingTrigger - Trigger being edited.
 * @returns {{
 *   filterRows: import("vue").Ref<object[]>,
 *   filterOptions: import("vue").WritableComputedRef<object>,
 *   filterModeDisabled: import("vue").WritableComputedRef<boolean>,
 *   isFilterOptionsEmpty: () => boolean,
 *   initializeFilterRows: () => void,
 *   addRow: () => void,
 *   removeRow: (index: number) => void,
 *   onFilterTypeSelected: (index: number) => void,
 *   isMatchTypeDisabled: (row: object, matchValue: string) => boolean,
 *   updateRowValues: (rowIndex: number, values: string[]) => void,
 *   removeRowValue: (rowIndex: number, value: string) => void,
 *   saveFilterOptions: () => void,
 * }}
 * @example
 * const { filterRows, addRow, removeRow } = useTriggerFilterRows(editingTrigger);
 */
export function useTriggerFilterRows(editingTrigger) {
  const filterRows = ref([]);

  const filterOptions = computed({
    get: () => editingTrigger.value?.filter_options || emptyFilterOptions(),
    set: value => {
      editingTrigger.value = { ...editingTrigger.value, filter_options: value };
    },
  });

  const filterModeDisabled = computed({
    get: () => editingTrigger.value?.filter_options?.enabled !== true,
    set: value => {
      if (!editingTrigger.value?.filter_options) {
        editingTrigger.value = {
          ...editingTrigger.value,
          filter_options: { ...editingTrigger.value?.filter_options, enabled: !value },
        };
      } else {
        editingTrigger.value.filter_options.enabled = !value;
      }
    },
  });

  const isFilterOptionsEmpty = () =>
    Object.keys(filterOptions.value)
      .filter(key => Array.isArray(filterOptions.value[key]))
      .every(key => filterOptions.value[key].length === 0);

  const initializeFilterRows = () => {
    filterRows.value = [];

    if (isFilterOptionsEmpty()) {
      filterRows.value.push({
        filterType: null,
        matchType: null,
        values: [],
        availableValues: [],
      });
      return;
    }

    Object.keys(filterOptions.value).forEach(filterType => {
      if (
        Array.isArray(filterOptions.value[filterType]) &&
        filterOptions.value[filterType].length > 0
      ) {
        filterOptions.value[filterType].forEach(entry => {
          filterRows.value.push({
            filterType,
            matchType: entry.match_type,
            values: [...entry.values],
            availableValues: [...entry.values],
          });
        });
      }
    });
  };

  const saveFilterOptions = () => {
    const updatedFilters = { ...emptyFilterOptions(), enabled: filterOptions.value.enabled };
    filterRows.value.forEach(row => {
      if (!row.filterType) return;
      updatedFilters[row.filterType].push({ match_type: row.matchType, values: [...row.values] });
    });
    editingTrigger.value = {
      ...editingTrigger.value,
      filter_options: { ...editingTrigger.value.filter_options, ...updatedFilters },
    };
  };

  const addRow = () => {
    filterRows.value.push({
      filterType: "",
      matchType: null,
      values: [],
      availableValues: [],
    });
  };

  const removeRow = index => {
    filterRows.value.splice(index, 1);
    saveFilterOptions();
  };

  const onFilterTypeSelected = index => {
    filterRows.value[index].matchType = null;
    filterRows.value[index].values = [];
    filterRows.value[index].availableValues = [];
  };

  const isMatchTypeDisabled = (row, matchValue) =>
    filterRows.value
      .filter(r => r.filterType === row.filterType && r !== row)
      .map(r => r.matchType)
      .includes(matchValue);

  const updateRowValues = (rowIndex, values) => {
    const row = filterRows.value[rowIndex];
    row.values = values;
    row.availableValues = [...new Set([...(row.availableValues || []), ...values])];
    saveFilterOptions();
  };

  /**
   * Permanently drops a value from a row's free-text options — unlike unchecking a value
   * (which just deselects it, see `updateRowValues`), this removes it from `availableValues`
   * too, so it stops being offered as an option at all.
   *
   * @param {number} rowIndex - Index into `filterRows`.
   * @param {string} value - Value to drop.
   * @example
   * removeRowValue(0, "/old-path");
   */
  const removeRowValue = (rowIndex, value) => {
    const row = filterRows.value[rowIndex];
    row.values = row.values.filter(v => v !== value);
    row.availableValues = (row.availableValues || []).filter(v => v !== value);
    saveFilterOptions();
  };

  return {
    filterRows,
    filterOptions,
    filterModeDisabled,
    isFilterOptionsEmpty,
    initializeFilterRows,
    addRow,
    removeRow,
    onFilterTypeSelected,
    isMatchTypeDisabled,
    updateRowValues,
    removeRowValue,
    saveFilterOptions,
  };
}
